import { useState } from "react";
import axios from "axios";
import { X, Lock, User, Hash, Calendar, Shield, Utensils, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Slider } from "./ui/slider";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSICIONES = [
  { value: "Golero", label: "Golero", emoji: "🧤" },
  { value: "Defensa", label: "Defensa", emoji: "🛡️" },
  { value: "Volante", label: "Volante", emoji: "⚙️" },
  { value: "Delantero", label: "Delantero", emoji: "⚽" },
];

// Ratón modal component
const RatonModal = ({ isOpen, onClose, onConfirm }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-[#3a3a3a] border border-white/10 text-white max-w-sm mx-auto"
        data-testid="raton-modal"
      >
        <div className="text-center py-4">
          <div className="text-6xl mb-4">🐀</div>
          <h3 className="font-heading text-2xl font-bold text-lactal-gold uppercase mb-2">
            ¡Ojo!
          </h3>
          <p className="text-gray-300 text-lg mb-6">
            Te perdés la comida por <span className="text-lactal-gold font-bold">ratatuille!</span>
          </p>
          <Button
            onClick={onConfirm}
            className="btn-primary w-full"
            data-testid="raton-aceptar-btn"
          >
            <span>Aceptar igual</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Función para obtener mensaje y tipo según nivel
const getNivelInfo = (nivel) => {
  if (nivel >= 1 && nivel <= 20) {
    return {
      mensaje: "Ni mi abuela, ni Jallan se puntuaría en esos valores, dale papá, ¡vos sos más que eso!",
      tipo: "error",
      bloquea: true
    };
  } else if (nivel >= 21 && nivel <= 40) {
    return {
      mensaje: "Vamos titán, los 2 sabemos que sos mucho más que eso, ¡no te hagas el humilde!",
      tipo: "error",
      bloquea: true
    };
  } else if (nivel >= 41 && nivel <= 50) {
    return {
      mensaje: "Bueno, podrías valorarte más, pero está bien, ¡te la llevo!",
      tipo: "success",
      bloquea: false
    };
  } else if (nivel >= 51 && nivel <= 60) {
    return {
      mensaje: "Ahora sí, ¡así te quiero!",
      tipo: "success",
      bloquea: false
    };
  } else if (nivel >= 61 && nivel <= 75) {
    return {
      mensaje: "¡Pará Cristiano! ¿Vos te viste cómo te movés en la cancha? Bajate un poco.",
      tipo: "error",
      bloquea: true
    };
  } else if (nivel >= 76 && nivel <= 85) {
    return {
      mensaje: "¡Así está el fútbol LPM! ¿Enserio creés que ese es tu puntaje? Dale titán, bajá un poco que sos una babosa.",
      tipo: "error",
      bloquea: true
    };
  } else if (nivel >= 86 && nivel <= 100) {
    return {
      mensaje: "¡PEEEHHHH! ¿Enserio?? ¡Ni Julito Salvatore se animó a tanto! Bajá un cambio titán que no podés correr ni la cortina.",
      tipo: "error",
      bloquea: true
    };
  }
  return { mensaje: "", tipo: "neutral", bloquea: true };
};

// Componente para mostrar mensaje del nivel
const NivelMessage = ({ nivel, show }) => {
  if (!show) return null;
  
  const { mensaje, tipo } = getNivelInfo(nivel);
  
  if (!mensaje) return null;
  
  const bgColor = {
    error: "bg-red-900/30 border-red-500/30 text-red-300",
    success: "bg-green-900/30 border-green-500/30 text-green-300",
    neutral: "bg-gray-900/30 border-gray-500/30 text-gray-300"
  }[tipo];
  
  return (
    <div className={`${bgColor} border rounded-lg p-3 text-sm mt-2`}>
      {mensaje}
    </div>
  );
};

const RegistrationModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    apodo: "",
    edad: "",
    telefono: "",
    posicion: "",
    nivel: 1,
    comida_final: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showRatonModal, setShowRatonModal] = useState(false);
  const [nivelTouched, setNivelTouched] = useState(false);

  // Verificar si el formulario está completo y válido
  const isFormValid = () => {
    const { nombre, apellido, edad, telefono, posicion, nivel } = formData;
    
    // Campos obligatorios
    if (!nombre.trim()) return false;
    if (!apellido.trim()) return false;
    if (!edad || edad < 18 || edad > 99) return false;
    if (!telefono.trim()) return false;
    if (!posicion) return false;
    
    // Nivel debe estar tocado y en rango válido (41-60)
    if (!nivelTouched) return false;
    const nivelInfo = getNivelInfo(nivel);
    if (nivelInfo.bloquea) return false;
    
    return true;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombre.trim()) newErrors.nombre = "El nombre es requerido";
    if (!formData.apellido.trim()) newErrors.apellido = "El apellido es requerido";
    if (!formData.edad || formData.edad < 18 || formData.edad > 99) {
      newErrors.edad = "Edad inválida (18-99)";
    }
    if (!formData.telefono.trim()) newErrors.telefono = "El teléfono es requerido";
    if (!formData.posicion) newErrors.posicion = "Seleccioná una posición";
    
    // Validar nivel
    if (!nivelTouched) {
      newErrors.nivel = "Movés el slider para seleccionar tu nivel";
    } else {
      const nivelInfo = getNivelInfo(formData.nivel);
      if (nivelInfo.bloquea) {
        newErrors.nivel = "Ajustá tu nivel para continuar";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/participantes`, {
        ...formData,
        edad: parseInt(formData.edad),
        nivel: parseInt(formData.nivel),
      });
      
      // Reset form
      setFormData({
        nombre: "",
        apellido: "",
        apodo: "",
        edad: "",
        telefono: "",
        posicion: "",
        nivel: 1,
        comida_final: false,
      });
      setNivelTouched(false);
      setErrors({});
      onSuccess();
    } catch (error) {
      console.error("Error al inscribirse:", error);
      setErrors({ submit: "Uff, pegó en el palo. Intentá de nuevo." });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Si no va a la comida, mostrar el modal del ratón
    if (!formData.comida_final) {
      setShowRatonModal(true);
      return;
    }
    
    await submitForm();
  };

  const handleRatonConfirm = async () => {
    setShowRatonModal(false);
    await submitForm();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent 
          className="bg-[#3a3a3a] border border-white/10 text-white max-w-md mx-auto max-h-[90vh] overflow-y-auto"
          data-testid="registration-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-tight text-center">
              <span className="text-lactal-gold">Inscripción</span> al Torneo
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="nombre" className="text-gray-300 flex items-center gap-2">
                <User size={16} />
                Nombre *
              </Label>
              <Input
                id="nombre"
                type="text"
                value={formData.nombre}
                onChange={(e) => handleChange("nombre", e.target.value)}
                className="input-dark"
                placeholder="Tu nombre"
                data-testid="input-nombre"
              />
              {errors.nombre && (
                <p className="text-red-400 text-sm">{errors.nombre}</p>
              )}
            </div>

            {/* Apellido */}
            <div className="space-y-2">
              <Label htmlFor="apellido" className="text-gray-300 flex items-center gap-2">
                <User size={16} />
                Apellido *
              </Label>
              <Input
                id="apellido"
                type="text"
                value={formData.apellido}
                onChange={(e) => handleChange("apellido", e.target.value)}
                className="input-dark"
                placeholder="Tu apellido"
                data-testid="input-apellido"
              />
              {errors.apellido && (
                <p className="text-red-400 text-sm">{errors.apellido}</p>
              )}
            </div>

            {/* Apodo */}
            <div className="space-y-2">
              <Label htmlFor="apodo" className="text-gray-300 flex items-center gap-2">
                <Hash size={16} />
                Apodo
              </Label>
              <Input
                id="apodo"
                type="text"
                value={formData.apodo}
                onChange={(e) => handleChange("apodo", e.target.value)}
                className="input-dark"
                placeholder="¿Cómo te dicen?"
                data-testid="input-apodo"
              />
            </div>

            {/* Edad */}
            <div className="space-y-2">
              <Label htmlFor="edad" className="text-gray-300 flex items-center gap-2">
                <Calendar size={16} />
                Edad *
              </Label>
              <Input
                id="edad"
                type="number"
                min="18"
                max="99"
                value={formData.edad}
                onChange={(e) => handleChange("edad", e.target.value)}
                className="input-dark"
                placeholder="Tu edad"
                data-testid="input-edad"
              />
              {errors.edad && (
                <p className="text-red-400 text-sm">{errors.edad}</p>
              )}
            </div>

            {/* Teléfono */}
            <div className="space-y-2">
              <Label htmlFor="telefono" className="text-gray-300 flex items-center gap-2">
                <Phone size={16} />
                Teléfono *
              </Label>
              <Input
                id="telefono"
                type="tel"
                value={formData.telefono}
                onChange={(e) => handleChange("telefono", e.target.value)}
                className="input-dark"
                placeholder="Ej: 099 123 456"
                data-testid="input-telefono"
              />
              {errors.telefono && (
                <p className="text-red-400 text-sm">{errors.telefono}</p>
              )}
              <p className="text-xs text-gray-500">Para notificaciones del torneo</p>
            </div>

            {/* Posición */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Shield size={16} />
                Posición *
              </Label>
              <Select
                value={formData.posicion}
                onValueChange={(value) => handleChange("posicion", value)}
              >
                <SelectTrigger 
                  className="input-dark"
                  data-testid="select-posicion"
                >
                  <SelectValue placeholder="Seleccioná tu posición" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {POSICIONES.map((pos) => (
                    <SelectItem 
                      key={pos.value} 
                      value={pos.value}
                      className="text-white hover:bg-white/10 focus:bg-white/10"
                      data-testid={`option-${pos.value.toLowerCase()}`}
                    >
                      <span className="flex items-center gap-2">
                        <span>{pos.emoji}</span>
                        <span>{pos.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.posicion && (
                <p className="text-red-400 text-sm">{errors.posicion}</p>
              )}
            </div>

            {/* Nivel Futbolístico */}
            <div className="space-y-3 bg-lactal-darker p-4 rounded-lg border border-white/5">
              <div className="flex items-center justify-between">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Lock size={16} className="text-lactal-gold" />
                  Nivel Futbolístico *
                </Label>
                <span className="text-lactal-gold font-heading text-2xl font-bold">
                  {formData.nivel}
                </span>
              </div>
              
              <Slider
                value={[formData.nivel]}
                onValueChange={([value]) => handleChange("nivel", value)}
                onValueCommit={() => setNivelTouched(true)}
                min={1}
                max={100}
                step={1}
                className="w-full"
                data-testid="slider-nivel"
              />
              
              <div className="flex justify-between text-xs text-gray-500">
                <span>1 - Recién arranco</span>
                <span>100 - Messi veterano</span>
              </div>
              
              {/* Mensaje según el nivel - solo si ya tocó el slider */}
              <NivelMessage nivel={formData.nivel} show={nivelTouched} />
              
              <p className="text-xs text-gray-500 flex items-center gap-1 mt-2">
                <Lock size={12} />
                Esta información es privada y solo la ve la organización para armar equipos parejos.
              </p>
            </div>

            {/* Comida Final */}
            <div className="bg-green-900/30 p-4 rounded-lg border border-green-500/30">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="comida_final"
                  checked={formData.comida_final}
                  onCheckedChange={(checked) => handleChange("comida_final", checked)}
                  className="mt-1 border-green-500 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                  data-testid="checkbox-comida"
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="comida_final" 
                    className="text-white font-medium cursor-pointer flex items-center gap-2"
                  >
                    <Utensils size={18} className="text-green-400" />
                    Me quedo a la comida final
                  </Label>
                  <p className="text-green-400 text-sm mt-1">
                    Voy a abonar los <span className="font-bold">$450</span>
                  </p>
                </div>
              </div>
            </div>

            {errors.submit && (
              <p className="text-red-400 text-sm text-center bg-red-400/10 p-3 rounded">
                {errors.submit}
              </p>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 btn-secondary"
                data-testid="btn-cancelar"
              >
                <span>Cancelar</span>
              </Button>
              <Button
                type="submit"
                disabled={loading || !isFormValid()}
                className={`flex-1 ${isFormValid() ? 'btn-primary' : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'}`}
                data-testid="btn-inscribirse"
              >
                <span>{loading ? "Inscribiendo..." : "¡Inscribirse!"}</span>
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <RatonModal 
        isOpen={showRatonModal}
        onClose={() => setShowRatonModal(false)}
        onConfirm={handleRatonConfirm}
      />
    </>
  );
};

export default RegistrationModal;
